import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Printer, Mail, Download, Pencil, Share2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_7b5007eb-b971-4941-9399-b2d87f9cbac2/artifacts/1lsetayu_barmann%20logo.png";

const STATUS_CLASS = {
  draft: "bm-badge-draft",
  sent: "bm-badge-sent",
  paid: "bm-badge-paid",
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
    } catch {
      navigate("/invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const setStatus = async (status) => {
    try {
      await api.post(`/invoices/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handlePrint = () => window.print();

  const copyShareLink = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/share`);
      const url = `${window.location.origin}/p/invoice/${data.share_token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Customer link copied to clipboard");
      } catch {
        window.prompt("Copy this customer link:", url);
      }
    } catch (err) {
      toast.error("Could not generate share link");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Barmann-${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const emailInvoice = async () => {
    if (!inv?.customer?.email) {
      toast.error("No customer email on file. Add an email to this customer first.");
      return;
    }
    // Download the PDF first so the user can attach it
    await downloadPdf();
    const subject = encodeURIComponent(`Invoice ${inv.number} from Barmann Lawn Service`);
    const body = encodeURIComponent(
      `Hi ${inv.customer.name},\n\nPlease find attached your invoice ${inv.number} for $${Number(inv.total).toFixed(2)}.\nThe PDF has been downloaded to your device — please attach it to this email before sending.\n\nThank you for choosing Barmann Lawn Service.\n\n— Barmann Lawn Service\n(678) 559-6163`
    );
    // Small delay so the download starts before mail client opens
    setTimeout(() => {
      window.location.href = `mailto:${inv.customer.email}?subject=${subject}&body=${body}`;
    }, 400);
  };

  if (loading || !inv) return <div className="overline">Loading…</div>;

  return (
    <div data-testid="invoice-detail-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3 print:hidden">
        <Link to="/invoices" className="inline-flex items-center gap-2 text-sm text-[#8ba394] hover:text-[#eae6db]">
          <ArrowLeft className="w-4 h-4" /> Back to invoices
        </Link>
        <div className="flex gap-2 flex-wrap">
          <Select value={inv.status} onValueChange={setStatus}>
            <SelectTrigger className="bm-input min-w-[130px]" data-testid="invoice-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141c18] border-[#233129] text-[#eae6db]">
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={() => navigate(`/invoices?edit=${id}`)}
            className="bm-btn bm-btn-outline"
            data-testid="edit-invoice-button"
          >
            <Pencil className="w-4 h-4" /> Edit
          </button>
          <button onClick={copyShareLink} className="bm-btn bm-btn-secondary" data-testid="share-invoice-button">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button onClick={downloadPdf} className="bm-btn bm-btn-secondary" data-testid="download-pdf-button">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button onClick={emailInvoice} className="bm-btn bm-btn-secondary" data-testid="email-invoice-button">
            <Mail className="w-4 h-4" /> Email
          </button>
          <button onClick={handlePrint} className="bm-btn bm-btn-primary" data-testid="print-invoice-button">
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      <div className="bm-print bm-invoice-paper mx-auto max-w-3xl p-10 shadow-2xl" data-testid="invoice-paper">
        <div className="flex items-start justify-between mb-10 pb-6 border-b-2 border-[#0a0f0d]/20">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Barmann Lawn Service" className="w-20 h-20 object-contain" />
            <div>
              <div className="font-display font-black text-2xl tracking-tight">BARMANN</div>
              <div className="text-xs uppercase tracking-widest font-bold">Lawn Service</div>
              <div className="text-xs mt-1 font-mono">(678) 559-6163</div>
              <div className="text-xs font-mono">barmannlawnservices@yahoo.com</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl font-black tracking-tight">INVOICE</div>
            <div className="font-mono text-sm mt-1">#{inv.number}</div>
            <div className="text-xs mt-2">Issued {inv.issued_date}</div>
            <div className={`bm-badge ${STATUS_CLASS[inv.status]} mt-2`}>{inv.status}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1 opacity-60">Bill To</div>
            <div className="font-display font-bold text-lg">{inv.customer?.name || "—"}</div>
            {inv.customer?.address && <div className="text-sm mt-1 whitespace-pre-line">{inv.customer.address}</div>}
            {inv.customer?.phone && <div className="text-sm mt-1 font-mono">{inv.customer.phone}</div>}
            {inv.customer?.email && <div className="text-sm font-mono">{inv.customer.email}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1 opacity-60">Service Area</div>
            <div className="text-sm">Loganville & Surrounding Area</div>
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1 opacity-60 mt-3">Amount Due</div>
            <div className="font-display font-black text-3xl text-[#C83E2D]">${Number(inv.total).toFixed(2)}</div>
          </div>
        </div>

        <table className="w-full mb-8">
          <thead>
            <tr className="border-b-2 border-[#0a0f0d]/20">
              <th className="text-left text-[10px] uppercase tracking-widest font-bold pb-2">Description</th>
              <th className="text-right text-[10px] uppercase tracking-widest font-bold pb-2 w-16">Qty</th>
              <th className="text-right text-[10px] uppercase tracking-widest font-bold pb-2 w-24">Price</th>
              <th className="text-right text-[10px] uppercase tracking-widest font-bold pb-2 w-24">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.line_items.map((li, idx) => (
              <tr key={idx} className="border-b border-[#0a0f0d]/10">
                <td className="py-3 text-sm">{li.description}</td>
                <td className="text-right font-mono py-3 text-sm">{li.quantity}</td>
                <td className="text-right font-mono py-3 text-sm">${Number(li.unit_price).toFixed(2)}</td>
                <td className="text-right font-mono py-3 text-sm font-bold">
                  ${(Number(li.quantity) * Number(li.unit_price)).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mb-8">
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between font-display font-black text-xl border-t-2 border-[#0a0f0d] pt-2 mt-2">
              <span>Total</span><span className="font-mono text-[#C83E2D]">${Number(inv.total).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {inv.notes && (
          <div className="mb-8 p-4 bg-[#0a0f0d]/5 border-l-4 border-[#C83E2D]">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-1 opacity-60">Notes</div>
            <div className="text-sm whitespace-pre-line">{inv.notes}</div>
          </div>
        )}

        <div className="text-center text-xs opacity-60 mt-10 pt-6 border-t border-[#0a0f0d]/20">
          Thank you for your business. Owned & operated by a dedicated Firefighter and Paramedic.
        </div>
      </div>
    </div>
  );
}
